import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import RouteKey from './key';
import View from '../pages/appeal/View';
import Receipt from '../pages/appeal/Receipt';
import Summary from '../pages/appeal/Summary';
import SelectSubject from '../pages/appeal/SelectSubject';
import ReviewAppeal from '../pages/appeal/ReviewAppeal';
import AppealImportantNotes from '../pages/appeal/AppealImportantNotes';
import { Role } from '../models';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import CoordinatorAppealListing from '../pages/appeal/CoordinatorAppealListing';
import CoordinatorAppealNotes from '../pages/appeal/CoordinatorAppealNotes';
import CoordinatorSelectSubject from '../pages/appeal/CoordinatorSelectSubject';
import CoordinatorReviewAppeal from '../pages/appeal/CoordinatorReviewAppeal/index';
import CoordinatorAppealSummary from '../pages/appeal/CoordinatorAppealSummary';
import CoordinatorSelectCandidate from '../pages/appeal/CoordinatorSelectCandidate';
import CoordinatorAppealReceiptAndPaymentLink from '../pages/appeal/CoordinatorAppealReceiptAndPaymentLink';

const { error: errorRoute } = RouteKey.child;
const {
  importantNotes,
  receipt,
  summary,
  selectCandidate,
  selectSubject,
  reviewApplication,
  applications,
  applicationsCoordinator,
} = RouteKey.appeal.child;
export const AppealNavigationContext = React.createContext<{
  toSummary: string;
}>({
  toSummary: '',
});

export const useAppealNavigation = () => useContext(AppealNavigationContext);

export const AppealRoute = () => {
  const { url, path } = useRouteMatch();
  const { t } = useTranslation();
  const appealResultTitle = t('appeal->>AppealRoute.AppealReviewResultsDetails');
  const reviewTitle = t('appeal->>AppealRoute.Appeal for Review of Results');

  const routeSteps = [
    {
      label: t('appeal->>RegisterAppeal.Select Subject(s)'),
    },
    {
      label: t('appeal->>RegisterAppeal.Review Application'),
    },
  ];

  const coordinatorRouteSteps = [
    {
      label: t('appeal->>RegisterAppeal.Select Candidate'),
    },
    {
      label: t('appeal->>RegisterAppeal.Select Subject(s)'),
    },
    {
      label: t('appeal->>RegisterAppeal.Review Application'),
    },
  ];

  const basePath = `${path}/${applications.route}`;

  const basePathCoordinator = `${path}/${applicationsCoordinator.route}`;

  const contextValue = useMemo(
    () => ({
      toSummary: `${url}/${applications.route}/${summary.route}`,
    }),
    [url]
  );

  return (
    <AppealNavigationContext.Provider value={contextValue}>
      <Switch>
        <Route path={`${basePath}/${importantNotes.route}`}>
          <AppealImportantNotes title={reviewTitle} />
        </Route>
        <Route path={`${basePath}/${selectSubject.route}`}>
          <SelectSubject title={reviewTitle} routeSteps={routeSteps} />
        </Route>
        <Route path={`${basePath}/${reviewApplication.route}`}>
          <ReviewAppeal title={reviewTitle} routeSteps={routeSteps} />
        </Route>
        <Route path={`${basePath}/${summary.route}`}>
          <Summary title={appealResultTitle} />
        </Route>
        <Route path={`${basePath}/${receipt.route}`}>
          <Receipt />
        </Route>
        <Route path={`${basePath}`}>
          <View title={reviewTitle} />
        </Route>
        {/** Coordinator Appeal */}
        <PrivateRoute exact path={`${basePathCoordinator}`} roles={[Role.coordinator]}>
          <CoordinatorAppealListing title={reviewTitle} />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/${importantNotes.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorAppealNotes title={reviewTitle} />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/${selectCandidate.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorSelectCandidate title={reviewTitle} routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/:uuid/${selectSubject.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorSelectSubject title={reviewTitle} routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/:uuid/${reviewApplication.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorReviewAppeal title={reviewTitle} routeSteps={coordinatorRouteSteps} />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/:uuid/${receipt.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorAppealReceiptAndPaymentLink />
        </PrivateRoute>
        <PrivateRoute
          path={`${basePathCoordinator}/:uuid/${summary.route}`}
          roles={[Role.coordinator]}
        >
          <CoordinatorAppealSummary />
        </PrivateRoute>

        <Route path="*" exact>
          <Redirect to={`/${errorRoute}`} />
        </Route>
      </Switch>
    </AppealNavigationContext.Provider>
  );
};

export default AppealRoute;
